import { Business } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { useEffect, useState } from "react";
import { TenantViewModel, UserClient } from "../../api/api";
import { createScopedLogger } from "../../common";
import ListWithFilter from "../../common/components/ListWithFilter";
import { useApiClient, useToastService } from "../../common/hooks";

const logger = createScopedLogger("TenantSelectorDialog");

interface TenantSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  onTenantChanged: (tenant: TenantViewModel) => void;
  availableTenants?: TenantViewModel[];
}

const TenantSelectorDialog: React.FC<TenantSelectorDialogProps> = ({ open, onClose, onTenantChanged, availableTenants }) => {
  const [tenants, setTenants] = useState<TenantViewModel[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const userClient = useApiClient(UserClient);
  const toast = useToastService();

  const loadTenants = React.useCallback(async () => {
    // If availableTenants are provided, use them instead of fetching
    if (availableTenants) {
      setTenants(availableTenants);
      return;
    }

    // Otherwise, fetch from API (fallback for backwards compatibility)
    setIsLoading(true);
    try {
      const userData = await userClient.getUser();
      setTenants(userData.tenants || []);
    } catch (error) {
      toast.error("Failed to load tenants");
      logger.error("Error loading tenants:", error);
    } finally {
      setIsLoading(false);
    }
  }, [userClient, toast, availableTenants]);

  useEffect(() => {
    if (open) {
      loadTenants();
    }
  }, [open, loadTenants]);

  const handleTenantSelect = (tenant: TenantViewModel) => {
    setSelectedTenantId(tenant.id);
  };

  const handleConfirm = async () => {
    if (!selectedTenantId) {
      toast.info("Please select a tenant");
      return;
    }

    const selectedTenant = tenants.find((t) => t.id === selectedTenantId);
    if (!selectedTenant) {
      toast.error("Selected tenant not found");
      return;
    }

    setIsSaving(true);
    try {
      await userClient.setTenant(selectedTenantId);
      toast.success("Tenant changed successfully");
      onTenantChanged(selectedTenant);
      onClose();
    } catch (error) {
      toast.error("Failed to change tenant");
      logger.error("Error changing tenant:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setSelectedTenantId(undefined);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Tenant</DialogTitle>
      <DialogContent>
        <ListWithFilter
          items={tenants}
          isLoading={isLoading}
          selectedItemId={selectedTenantId}
          onSelected={handleTenantSelect}
          placeholder="Search tenants..."
          displayNameProperty="code"
          keyProperty="id"
          maxHeight="400px"
          emptyMessage="No tenants available"
          icon={() => <Business />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!selectedTenantId || isSaving}>
          {isSaving ? "Changing..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantSelectorDialog;
